import React, { useState } from 'react';
import { DrawingToolKey, DrawingToolConfig } from '../../types';
import { DrawingToolsList, DEFAULT_DRAWING_CONFIG } from '../../constants';
import IconButton from '@mui/material/IconButton';
import { ReactComponent as MoveIcon } from '../../../../assets/icons/StockChart/pan_tool_24px.svg';
import { ReactComponent as LineIcon } from '../../../../assets/icons/StockChart/Union1.svg';
import { ReactComponent as RetracementIcon } from '../../../../assets/icons/StockChart/icon_drawtool_retracement.svg';
import { ReactComponent as FansIcon } from '../../../../assets/icons/StockChart/icon_drawtool_fan.svg';
import { ReactComponent as ArcsIcon } from '../../../../assets/icons/StockChart/icon_drawtool_arcs.svg';
import { ReactComponent as ResetIcon } from '../../../../assets/icons/StockChart/replay_24px.svg';
import { ReactComponent as ArrowDownIcon } from '../../../../assets/icons/arrow-down-s-line.svg';
import SettingsPopup from '../common/SettingsPopup';
import SettingsCheckbox from '../common/SettingsCheckbox';
import LineStyleEditor from '../common/LineStyleEditor';
import '../../../../Components/Common/typography.css';
import './DrawingToolsSettings.css';

interface DrawingToolsSettingsProps {
  activeTool: DrawingToolKey | null;
  moveEnabled: boolean;
  drawingConfigs: Record<DrawingToolKey, DrawingToolConfig>;
  onToolChange: (tool: DrawingToolKey | null) => void;
  onMoveToggle: (enabled: boolean) => void;
  onConfigChange: (tool: DrawingToolKey, config: DrawingToolConfig) => void;
}

const TOOL_ICONS: Record<DrawingToolKey, React.ReactNode> = {
  line: <LineIcon />,
  retracement: <RetracementIcon />,
  fans: <FansIcon />,
  arcs: <ArcsIcon />,
};

const DrawingToolsSettings: React.FC<DrawingToolsSettingsProps> = ({
  activeTool,
  moveEnabled,
  drawingConfigs,
  onToolChange,
  onMoveToggle,
  onConfigChange,
}) => {
  const [expandedTools, setExpandedTools] = useState<Record<string, boolean>>({
    line: true,
    retracement: false,
    fans: false,
    arcs: false,
  });

  const toggleExpand = (key: string) => {
    setExpandedTools((prev) => {
      const allClosed = {
        line: false,
        retracement: false,
        fans: false,
        arcs: false,
      };
      return { ...allClosed, [key]: !prev[key] };
    });
  };

  const handleToolSelect = (key: DrawingToolKey) => {
    const next = activeTool === key ? null : key;
    onToolChange(next);
    if (next !== null) onMoveToggle(false);
    setExpandedTools({
      line: false,
      retracement: false,
      fans: false,
      arcs: false,
      [key]: true,
    });
  };

  const handleResetTool = (key: DrawingToolKey) => {
    onConfigChange(key, { ...DEFAULT_DRAWING_CONFIG });
  };

  return (
    <SettingsPopup className="drawing-tools-popup">
      {/* Move */}
      <div
        className={`drawing-tools-row${moveEnabled ? ' drawing-tools-row-active' : ''}`}
        onClick={() => {
          const next = !moveEnabled;
          if (next) onToolChange(null);
          onMoveToggle(next);
        }}
      >
        <SettingsCheckbox
          checked={moveEnabled}
          onChange={() => {
            const next = !moveEnabled;
            if (next) onToolChange(null);
            onMoveToggle(next);
          }}
        />
        <span className="drawing-tools-icon">
          <MoveIcon />
        </span>
        <span className="body-m-regular">Move</span>
      </div>

      {/* Drawing tools */}
      {DrawingToolsList.map(({ key, label }) => {
        const isActive = activeTool === key;
        const isExpanded = expandedTools[key];
        const config = drawingConfigs[key];

        return (
          <div key={key} className="drawing-tools-section">
            <div
              className={`drawing-tools-row${isActive ? ' drawing-tools-row-active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (!isActive) {
                  handleToolSelect(key);
                } else {
                  toggleExpand(key);
                }
              }}
            >
              <SettingsCheckbox checked={isActive} onChange={() => handleToolSelect(key)} />
              <span className="drawing-tools-icon">{TOOL_ICONS[key]}</span>
              <span className="body-m-regular">{label}</span>
              <span className="drawing-tools-actions">
                {isExpanded && isActive && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResetTool(key);
                    }}
                    sx={{
                      padding: '2px',
                      '& svg path': { fill: 'var(--accent-primary)' },
                    }}
                  >
                    <ResetIcon />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isActive) {
                      handleToolSelect(key);
                    } else {
                      toggleExpand(key);
                    }
                  }}
                  sx={{ padding: '2px' }}
                  className={isExpanded ? 'drawing-tools-arrow-up' : ''}
                >
                  <ArrowDownIcon />
                </IconButton>
              </span>
            </div>

            {isExpanded && isActive && (
              <LineStyleEditor
                color={config.color}
                thickness={config.thickness}
                style={config.style}
                onColorChange={(color) =>
                  onConfigChange(key, { ...config, color })
                }
                onThicknessChange={(thickness) =>
                  onConfigChange(key, { ...config, thickness })
                }
                onStyleChange={(style) =>
                  onConfigChange(key, { ...config, style })
                }
              />
            )}
          </div>
        );
      })}
    </SettingsPopup>
  );
};

export default DrawingToolsSettings;
